import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createCategory,
  deleteCategory,
  getCategoriesByUser,
  updateCategory,
  type CategoryInput,
} from '../api/categories.ts'
import type { Category } from '../types.ts'

export function useCategories(userId: number) {
  return useQuery<Category[]>({
    queryKey: ['categories', userId],
    queryFn: () => getCategoriesByUser(userId),
    select: (data) => [...data].sort((a, b) => a.name.localeCompare(b.name)),
  })
}

export function useCreateCategory(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<CategoryInput, 'user_id'>) =>
      createCategory({ ...data, user_id: userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories', userId] }),
  })
}

export function useUpdateCategory(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number
      data: Partial<Omit<CategoryInput, 'user_id'>>
    }) => updateCategory(id, { ...data, user_id: userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories', userId] }),
  })
}

export function useDeleteCategory(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteCategory(id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories', userId] }),
  })
}
